import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, UsersIcon, ClockIcon, BadgeCheckIcon, BuildingIcon, ScaleIcon } from 'lucide-react';
export function WhatWeDo() {
  const credentials = [{
    icon: BadgeCheckIcon,
    label: 'CJIS Authorized'
  }, {
    icon: BuildingIcon,
    label: 'State of Maryland Certified'
  }, {
    icon: ScaleIcon,
    label: 'COMAR Regulation 12.15.05'
  }];
  const values = [{
    icon: ShieldCheckIcon,
    title: 'Quality Service',
    description: 'Our Live Scan fingerprinting service provides the highest quality and most efficient service for government agencies, employers, and individuals.'
  }, {
    icon: UsersIcon,
    title: 'Certified Technicians',
    description: 'Our highly trained and certified fingerprint technicians ensure your fingerprinting process is smooth, quick, and accurate.'
  }, {
    icon: ClockIcon,
    title: 'Quick & Hassle-Free',
    description: 'We understand the value of your time and strive to make the fingerprinting process as quick and hassle-free as possible.'
  }];
  return <section id="about" className="py-24 bg-white" aria-labelledby="about-heading" data-id="element-242">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-243">
        {/* About Us - Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-24" data-id="element-244">
          {/* Left - Image */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.7,
          ease: 'easeOut'
        }} className="relative" data-id="element-245">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl" data-id="element-246">
              <img src="https://cdn.magicpatterns.com/uploads/9tU3cLyW8VBywMYVeZzqov/about-image.png" alt="Ambient Fingerprinting - Secure digital technology" className="w-full h-auto object-cover aspect-[4/3]" data-id="element-247" />
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#1E1F55]/30 to-transparent" data-id="element-248" />
            </div>

            {/* Floating credential badge */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4,
            duration: 0.5
          }} className="absolute -bottom-6 -right-4 sm:right-6 bg-white rounded-xl shadow-xl border border-gray-100 px-5 py-4 flex items-center gap-3" data-id="element-249">
              <div className="bg-[#4AA6CF]/10 p-2.5 rounded-lg" data-id="element-250">
                <ShieldCheckIcon className="h-6 w-6 text-[#4AA6CF]" aria-hidden="true" data-id="element-251" />
              </div>
              <div data-id="element-252">
                <p className="text-2xl font-bold text-[#1E1F55]" data-id="element-253">10+</p>
                <p className="text-sm text-gray-600 font-medium" data-id="element-254">
                  Years of Experience
                </p>
              </div>
            </motion.div>
          </motion.div>

          {/* Right - Text Content */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.7,
          ease: 'easeOut'
        }} data-id="element-255">
            <span className="inline-block text-[#2B7A9E] font-semibold text-sm tracking-wider uppercase mb-3" data-id="element-256">
              About Us
            </span>
            <h2 id="about-heading" className="text-3xl md:text-4xl font-bold text-[#1E1F55] mb-6 leading-tight" data-id="element-257">
              Who We Are
            </h2>
            <div className="w-16 h-1 bg-[#4AA6CF] rounded-full mb-8" aria-hidden="true" data-id="element-258"></div>

            <div className="space-y-5 text-gray-600 leading-relaxed" data-id="element-259">
              <p data-id="element-260">
                Ambient Solutions, LLC, operating under the trade name Ambient
                Fingerprinting Solution, is a premier provider of secure
                electronic fingerprinting services in Columbia, Maryland. Our
                commitment to quality and professionalism is unmatched, and we
                take pride in offering a range of services tailored to meet the
                diverse needs of our clients.
              </p>
              <p data-id="element-261">
                We are certified by the State of Maryland and authorized by CJIS
                (Criminal Justice Information Systems) to operate under COMAR
                Regulation 12.15.05 and the Criminal Procedure Article §10-221,
                Annotated Code of Maryland. This authorization allows us to
                submit fingerprints directly to the Maryland Criminal Justice
                Information System for criminal history records checks.
              </p>
              <p data-id="element-262">
                Ambient Fingerprinting Solution ensures the secure capture and
                transmission of electronic fingerprints for various purposes,
                including employment, certification, licensing, and other
                verification needs.
              </p>
            </div>

            {/* Credential Badges */}
            <div className="flex flex-wrap gap-3 mt-8" data-id="element-263">
              {credentials.map((cred, index) => <div key={index} className="flex items-center gap-2 px-4 py-2 bg-[#F0F4FF] rounded-full border border-[#E7ECF8]" data-id="element-264">
                  <cred.icon className="h-4 w-4 text-[#4AA6CF]" aria-hidden="true" data-id="element-265" />
                  <span className="text-sm font-medium text-[#1E1F55]" data-id="element-266">
                    {cred.label}
                  </span>
                </div>)}
            </div>
          </motion.div>
        </div>

        {/* Value Cards */}
        <div className="text-center mb-12" data-id="element-267">
          <h3 className="text-2xl md:text-3xl font-bold text-[#1E1F55] mb-4" data-id="element-268">
            What Sets Us Apart
          </h3>
          <div className="w-24 h-1 bg-[#4AA6CF] mx-auto rounded-full" aria-hidden="true" data-id="element-269"></div>
        </div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={{
        hidden: {
          opacity: 0
        },
        visible: {
          opacity: 1,
          transition: {
            staggerChildren: 0.2
          }
        }
      }} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-270">
          {values.map((card, index) => <motion.div key={index} variants={{
          hidden: {
            opacity: 0,
            y: 20
          },
          visible: {
            opacity: 1,
            y: 0,
            transition: {
              duration: 0.6,
              ease: 'easeOut'
            }
          }
        }} className="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 group" data-id="element-271">
              <div className="w-14 h-14 bg-[#F0F4FF] rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#1E1F55] transition-colors duration-300" data-id="element-272">
                <card.icon className="h-7 w-7 text-[#4AA6CF] group-hover:text-white transition-colors duration-300" aria-hidden="true" data-id="element-273" />
              </div>
              <h3 className="text-xl font-bold text-[#1E1F55] mb-4" data-id="element-274">
                {card.title}
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-275">
                {card.description}
              </p>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}